import { Component, Element, Event, EventEmitter, State, Watch, forceUpdate, h } from "@stencil/core";
import { CreateCaseResponse, TRANSACTION_STATES } from "../../api/publicApi/createCase";
import { getPublicApiClient, PublicApiClient } from "../../api/publicApi/PublicApiClient";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import { EnvConfig, getEnvConfig, setEnv } from "../../api/config";

@Component({
    tag: "ux-f-sis-create-case-connector",
    styleUrl: "sis-connector.css",
    shadow: false
})
export class SisConnector {
    @Element() host: HTMLUxFSisCreateCaseConnectorElement;

    @State() response: CreateCaseResponse | null = null;
    @State() errorMessage: string | null = null;
    @State() isRequestPending = false;
    @State() locale: string | null = null;

    /**
     * service case was created successfully
     */
    @Event({ eventName: "mfServiceCaseSuccess" }) mfServiceCaseSuccess: EventEmitter<void>;
    /**
     * service case creation was canceled
     */
    @Event({ eventName: "mfServiceCaseCancel" }) mfServiceCaseCancel: EventEmitter<void>;

    private parent: HTMLMfCreateServiceCaseElement | null = null;
    private apiClient: PublicApiClient | null = null;
    private i18nSubscriberId: string;
    private config: EnvConfig;
    private tbdbTokenReceived = false;
    private unsubscribeToTbdbToken: () => void;

    private onServiceCaseSubmit = async (event: CustomEvent) => {
        if (!this.apiClient) {
            return;
        }
        this.isRequestPending = true;
        if (this.tbdbTokenReceived) {
            this.apiClient
                .request<CreateCaseResponse>("/servicecall/createcase", { body: event.detail })
                .then((response) => {
                    this.response = response;
                })
                .catch((error) => {
                    this.errorMessage = error;
                })
                .finally(() => {
                    this.isRequestPending = false;
                });
        } else {
            this.errorMessage = "TBDB token not received. Case could not be created.";
            this.isRequestPending = false;
        }
    };

    private onSuccess() {
        this.mfServiceCaseSuccess.emit();
    }

    private onCancel() {
        this.mfServiceCaseCancel.emit();
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.parent = this.host.closest("mf-create-service-case");
        this.parent?.addEventListener("mfServiceCaseSubmit", this.onServiceCaseSubmit);
        this.locale = this.parent?.locale || null;
        const parentEnv = this.host.closest<HTMLMfCreateServiceCaseElement | HTMLMfCreateServiceCaseModalElement>(
            "mf-create-service-case, mf-create-service-case-modal"
        )?.env;
        if (parentEnv) {
            setEnv(parentEnv);
        }
        this.config = getEnvConfig();
        this.apiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.apiClient.subscribeToTbdbToken((event) => {
            if (event.success) {
                this.tbdbTokenReceived = true;
            } else {
                this.errorMessage = event.errorMessage || "TBDB token request failed";
            }
        });
    }

    disconnectedCallback() {
        this.parent?.removeEventListener("mfServiceCaseSubmit", this.onServiceCaseSubmit);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    render() {
        if (this.isRequestPending) {
            return <ux-loader variant={"spinner"}></ux-loader>;
        }
        if (this.response === null && this.errorMessage === null) {
            return null;
        }
        const t = (key: TranslationKey, options: unknown = undefined) =>
            i18nProvider.t(`mf-service-calls:${String(key)}`, options as Record<string, unknown>);
        if (this.response?.[0]?.transactionState === TRANSACTION_STATES.RECEIVED) {
            const serviceHours = this.response?.[0]?.serviceHours;
            const hasMonFri = Boolean(serviceHours?.MonFriStart && serviceHours?.MonFriEnd);
            const hasSat = serviceHours?.SatStart && serviceHours?.SatEnd;
            const monFriHtml = hasMonFri
                ? t("sisConnector.success.serviceTimes.MonFri", {
                      startTime: serviceHours?.MonFriStart,
                      endTime: serviceHours?.MonFriEnd
                  })
                : "";
            const satHtml = hasSat
                ? t("sisConnector.success.serviceTimes.Sat", {
                      startTime: serviceHours.SatStart,
                      endTime: serviceHours.SatEnd
                  })
                : "";
            // TODO: add texts for service hotline and technical guides

            return (
                <div>
                    <h1 class="ux-text-headline">{t("sisConnector.success.headline")}</h1>
                    {this.response[0]?.isCustomerOrderNumberRequired && <p>{t("sisConnector.success.orderNumberRequired")}</p>}
                    {this.response[0]?.isOutsideOfServiceHours && (
                        <div class="ux-text-normal">
                            <p>{t("sisConnector.success.outsideOfServiceHours")}</p>
                            {hasMonFri && <p innerHTML={`${monFriHtml} ${satHtml}`} />}
                        </div>
                    )}
                    {this.response[0]?.technicalServiceHotline && (
                        <p class="ux-text-normal">
                            {t("sisConnector.success.withTechnicalServiceHotline", {
                                hotline: this.response[0]?.technicalServiceHotline
                            })}
                        </p>
                    )}
                    {!this.response[0]?.technicalServiceHotline && (
                        <p class="ux-text-normal">{t("sisConnector.success.withoutTechnicalServiceHotline")}</p>
                    )}
                    <ux-button type="button" onClick={() => this.onSuccess()} formnovalidate>
                        {t("button.close")}
                    </ux-button>
                </div>
            );
        } else {
            return (
                <div>
                    <h1 class="ux-text-headline">{t("sisConnector.failed.headline")}</h1>
                    <p class="ux-text-normal">{this.errorMessage || t("sisConnector.failed.unknownError")}</p>
                    <ux-button type="button" onClick={() => this.onCancel()} formnovalidate>
                        {t("button.cancel")}
                    </ux-button>
                </div>
            );
        }
    }
}
